import React, { useCallback } from "react";
import { Text, View, Link } from "@react-pdf/renderer";
import type { Style } from "@react-pdf/types";
import { styles, spacing } from "components/Resume/ResumePDF/styles";
import { DEBUG_RESUME_PDF_FLAG } from "lib/constants";
import { DEFAULT_FONT_COLOR } from "lib/redux/settingsSlice";
import {
  ResumePDFLink,
  ResumePDFText,
} from "components/Resume/ResumePDF/common";
import { handleClick } from "lib/utils/navigator";


export const TemplateTwelvePDFSection = ({
  themeColor,
  heading,
  style = {},
  children,
}: {
  themeColor?: string;
  heading?: string;
  style?: Style;
  children: React.ReactNode;
}) =>{
  const handleClickCallback = useCallback(() => handleClick(heading), [heading]);
  return(
  <div
  onClick={handleClickCallback}
    style={{
      ...styles.flexCol,
      gap: spacing["2"],
      marginTop: spacing["5"],
      cursor:"pointer"
      // ...style,
    }}
  >
    {heading && (
      <View style={{ ...styles.flexCol, gap: spacing["1"] }}>
        <Text
        
        style={{
        fontWeight: "bold",
        letterSpacing: "0.3pt", // tracking-wide -> 0.025em * 12 pt = 0.3pt
        color: themeColor,
      }}
          debug={DEBUG_RESUME_PDF_FLAG}
        >
          {heading}
        </Text>
        {themeColor && (
          <View
            style={{
              height: "3pt",
              width: "100%",
              backgroundColor: themeColor,
              marginRight: spacing["3.5"],
            }}
            debug={DEBUG_RESUME_PDF_FLAG}
          />
        )}
      </View>
    )}
    {children}
  </div>
)};

export const ResumePDFDescriptionWithWhiteFont = ({
  items,
}: {
  items: string[];
}) => {
  // Check if items is defined and has at least one element
  if (!items || items.length === 0) {
    return null; // or render a placeholder, like <p>No descriptions available</p>
  }
  const newDescription = items.join("");
  const descriptionNodes = extractNodesFromHtml(newDescription);

  return (
    <View wrap={false}>
      {descriptionNodes.map((node, idx) => (
        <React.Fragment key={idx}>{renderNode(node)}</React.Fragment>
      ))}
    </View>
  );
};

const parseAndTransformHTML = (htmlString: string): React.ReactNode[] => {
  // Create a temporary DOM element to parse the HTML string
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, "text/html");
  const transformedChildren: React.ReactNode[] = [];

  // Recursive function to process and transform HTML nodes
  const transformNode = (node: Node) => {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const element = node as HTMLElement;

      switch (element.nodeName.toLowerCase()) {
        case "strong":
          return (
            <ResumePDFText key={Math.random()} bold={true} themeColor="white">
              {element.textContent}
            </ResumePDFText>
          );
        case "em":
          return (
            <ResumePDFText key={Math.random()} italic={true} themeColor="white">
              {element.textContent}
            </ResumePDFText>
          );
        case "u":
          return (
            <ResumePDFText
              key={Math.random()}
              underline={true}
              themeColor="white"
            >
              {element.textContent}
            </ResumePDFText>
          );
        case "a":
          const href = element.getAttribute("href");
          return (
            <ResumePDFLink
              isPDF={DEBUG_RESUME_PDF_FLAG as true}
              src={href || "#"}
              key={Math.random()}
            >
              <ResumePDFText themeColor="blue" underline={true}>
                {element.textContent}
              </ResumePDFText>
            </ResumePDFLink>
          );
        case "span":
          // Skip span elements
          return null;
        case "p":
          return (
            <View key={Math.random()}>
              <ResumePDFText themeColor="white">
                {Array.from(element.childNodes).map(transformNode)}
              </ResumePDFText>
              ;
            </View>
          );
        case "li":
          return (
            <View key={Math.random()} style={{ flexDirection: "row" }}>
              <ResumePDFText
                style={{
                  paddingLeft: 4, // adjust spacing as needed
                  paddingRight: 4, // adjust spacing as needed
                  lineHeight: 1.3,
                }}
                bold={true}
                themeColor="white"
              >
                {"•"}
              </ResumePDFText>
              <ResumePDFText
                style={{ lineHeight: 1.3, flexGrow: 1, flexBasis: 0 }}
                themeColor="white"
              >
                {Array.from(element.childNodes).map(transformNode)}
              </ResumePDFText>
            </View>
          );
        case "ol":
          return (
            <View key={Math.random()} style={{ flexDirection: "column" }}>
              {Array.from(element.childNodes).map((child, index) =>
                transformNode(child)
              )}
            </View>
          );
        default:
          return null;
      }
    }

    if (node.nodeType === Node.TEXT_NODE) {
      return node.textContent;
    }

    return null;
  };

  Array.from(doc.body.childNodes).forEach((child) => {
    const transformedNode = transformNode(child);
    if (transformedNode) {
      transformedChildren.push(transformedNode);
    }
  });

  return transformedChildren;
};

const renderNode = (node: Node, themeColor: string = "black"): React.ReactNode => {
  if (node.nodeType === Node.ELEMENT_NODE) {
    const element = node as Element;
    switch (element.nodeName.toLowerCase()) {
      case "p":
        return (
          <View>
            <ResumePDFText
              style={{ lineHeight: 1.3, flexGrow: 1 }}
              themeColor={themeColor}
            >
              {parseAndTransformHTML(element.innerHTML)}
              <br />
            </ResumePDFText>
          </View>
        );
        
      case "ol":
        return (
          <View style={{ ...styles.flexCol }}>
            {Array.from(element.childNodes).map((child, index) =>
              child.nodeName.toLowerCase() === "li" ? (
                <View key={index} style={{ ...styles.flexRow }}>
                  <ResumePDFText
                    style={{
                      paddingLeft: spacing["2"],
                      paddingRight: spacing["2"],
                      lineHeight: 1.3,
                    }}
                    bold={true}
                    themeColor={themeColor}
                  >
                    {"•"}
                  </ResumePDFText>
                  <ResumePDFText
                    style={{ lineHeight: 1.3, flexGrow: 1, flexBasis: 0 }}
                    themeColor={themeColor}
                  >
                    {parseAndTransformHTML((child as Element).innerHTML)}
                  </ResumePDFText>
                </View>
              ) : null
            )}
          </View>
        );
        
      case "li":
        return (
          <View style={{ ...styles.flexRow }}>
            <ResumePDFText
              style={{
                paddingLeft: spacing["2"],
                paddingRight: spacing["2"],
                lineHeight: 1.3,
              }}
              bold={true}
              themeColor={themeColor}
            >
              {"•"}
            </ResumePDFText>
            <ResumePDFText
              style={{
                lineHeight: 1.3,
                flexGrow: 1,
                flexBasis: 0,
              }}
              themeColor={themeColor}
            >
              {parseAndTransformHTML(element.innerHTML)}
            </ResumePDFText>
          </View>
        );
        
      default:
        return null;
    }
  }
  return null;
};


export const extractNodesFromHtml = (html: string) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  const nodes = Array.from(doc.body.childNodes);
  return nodes;
};

export const ResumePDFDescription = ({ items,themeColor}: { items: string[],themeColor?:string }) => {
  if (!items || items.length === 0) {
    return null; // or render a placeholder, like <p>No descriptions available</p>
  }
  const newDescription = items.join("");
  const descriptionNodes = extractNodesFromHtml(newDescription);

  return (
    <View wrap={false}>
      {descriptionNodes.map((node, idx) => (
        <React.Fragment key={idx}>{renderNode(node,themeColor)}</React.Fragment>
      ))}
    </View>
  );
};

// Enhanced version with optimized spacing and orphan heading prevention for TemplateTwelve
export const TemplateTwelvePDFSectionWithSubsections = ({
  themeColor,
  heading,
  style = {},
  children,
}: {
  themeColor?: string;
  heading?: string;
  style?: Style;
  children: React.ReactNode;
}) => {
  const handleClickCallback = useCallback(() => handleClick(heading), [heading]);
  
  // Group content to prevent orphan headings
  const groupedChildren = React.Children.toArray(children);
  const firstChild = groupedChildren[0];
  const restChildren = groupedChildren.slice(1);
  
  return (
    <div
      onClick={handleClickCallback}
      style={{
        ...styles.flexCol,
        gap: "9pt", // Optimized from spacing["2"] (24pt) to 9pt
        marginTop: "18pt", // Optimized from spacing["5"] (60pt) to 18pt
        cursor: "pointer"
      }}
    >
      {/* Wrap heading with first child only to prevent orphan headings */}
      {heading && firstChild && (
        <View
          style={{
            ...styles.flexCol,
            gap: "6pt", // Optimized spacing
          }}
          wrap={false}
        >
          {/* Heading with colored underline */}
          <View style={{ ...styles.flexCol, gap: "3pt" }}>
            <Text
                style={{
                fontWeight: "bold",
                letterSpacing: "0.3pt",
                color: "white", // White text for visibility on colored background
              }}
              debug={DEBUG_RESUME_PDF_FLAG}
            >
              {heading}
            </Text>
            {themeColor && (
              <View
                style={{
                  height: "2pt", // Reduced from 3pt
                  width: "100%",
                  backgroundColor: "white", // White underline for visibility
                  marginRight: "12pt", // Reduced from spacing["3.5"]
                }}
                debug={DEBUG_RESUME_PDF_FLAG}
              />
            )}
          </View>
          {/* First child inside the same container */}
          {firstChild}
        </View>
      )}
      
      {/* Heading only (when no children) */}
      {heading && !firstChild && (
        <View style={{ ...styles.flexCol, gap: "3pt" }}>
          <Text
            style={{
              fontWeight: "bold",
              letterSpacing: "0.3pt",
              color: "white",
            }}
            debug={DEBUG_RESUME_PDF_FLAG}
          >
            {heading}
          </Text>
          {themeColor && (
            <View
              style={{
                height: "2pt",
                width: "100%",
                backgroundColor: "white",
                marginRight: "12pt",
              }}
              debug={DEBUG_RESUME_PDF_FLAG}
            />
          )}
        </View>
      )}
      
      {/* Rest of children - can break across pages */}
      {restChildren.length > 0 && (
        <View
          style={{
            ...styles.flexCol,
            gap: "9pt",
          }}
        >
          {restChildren}
        </View>
      )}
    </div>
  );
};
